import LoadingRows from './LoadingRows.jsx';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TeamLogo from './TeamLogo';
import { getLeaders } from '../services/leaders';
import { ACTIVE_WOMENS_SEASON } from '../data/seasonConfig';
import '../styles/leaders.css';

const FAVORITES_STORAGE_KEY = 'volleywire.favoriteTeams.v1';
const FAVORITES_EVENT = 'volleywire:favorites-changed';

export function formatStatsThroughDate(value) {
  const iso = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const date = new Date(iso + 'T12:00:00Z');
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
  }).format(date) : '';
}

const FALLBACK_CATEGORIES = {
  individual: [
    { key: 'hitting-percentage', label: 'Hitting Percentage' },
    { key: 'kills-per-set', label: 'Kills Per Set' },
    { key: 'assists-per-set', label: 'Assists Per Set' },
    { key: 'aces-per-set', label: 'Aces Per Set' },
    { key: 'blocks-per-set', label: 'Blocks Per Set' },
    { key: 'digs-per-set', label: 'Digs Per Set' },
    { key: 'points-per-set', label: 'Points Per Set' },
    { key: 'total-kills', label: 'Total Kills', mode: 'totals' },
    { key: 'total-assists', label: 'Total Assists', mode: 'totals' },
    { key: 'total-digs', label: 'Total Digs', mode: 'totals' },
    { key: 'total-aces', label: 'Total Service Aces', mode: 'totals' },
    { key: 'total-blocks', label: 'Total Blocks', mode: 'totals' },
    { key: 'total-points', label: 'Total Points', mode: 'totals' },
  ],
  team: [
    { key: 'hitting-percentage', label: 'Hitting Percentage' },
    { key: 'kills-per-set', label: 'Kills Per Set' },
    { key: 'assists-per-set', label: 'Assists Per Set' },
    { key: 'aces-per-set', label: 'Aces Per Set' },
    { key: 'blocks-per-set', label: 'Blocks Per Set' },
    { key: 'digs-per-set', label: 'Digs Per Set' },
    {
      key: 'opponent-hitting-percentage',
      label: 'Opponent Hitting Percentage',
    },
    { key: 'total-kills', label: 'Total Kills', mode: 'totals' },
    { key: 'total-assists', label: 'Total Assists', mode: 'totals' },
    { key: 'total-digs', label: 'Total Digs', mode: 'totals' },
    { key: 'total-aces', label: 'Total Service Aces', mode: 'totals' },
    { key: 'total-blocks', label: 'Total Blocks', mode: 'totals' },
    { key: 'total-points', label: 'Total Points', mode: 'totals' },
  ],
};

export function leaderCategoryForType(category, type) {
  const available = FALLBACK_CATEGORIES[type === 'team' ? 'team' : 'individual'];
  if (available.some(item => item.key === category)) return category;
  return (available.find(item => category.startsWith('total-') ? item.mode === 'totals' : item.mode !== 'totals') || available[0]).key;
}

function readUrlState() {
  if (typeof window === 'undefined') {
    return {
      type: 'individual',
      category: FALLBACK_CATEGORIES.individual[0].key,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const type = params.get('leadersType') === 'team' ? 'team' : 'individual';
  const requestedCategory = String(params.get('leadersCategory') || '').trim();
  const categories = FALLBACK_CATEGORIES[type];
  const category = categories.some((item) => item.key === requestedCategory)
    ? requestedCategory
    : categories[0].key;

  return { type, category };
}

function writeUrlState({ type, category, mode = 'replace' }) {
  const url = new URL(window.location.href);
  url.searchParams.delete('sport');
  url.searchParams.set('leadersType', type);
  url.searchParams.set('leadersCategory', category);
  url.searchParams.delete('rankingSeason');
  url.searchParams.delete('standingsConference');
  url.searchParams.delete('team');
  url.searchParams.delete('teamSeason');
  url.searchParams.delete('match');
  url.hash = 'leaders';
  window.history[mode === 'push' ? 'pushState' : 'replaceState'](
    {},
    '',
    url.toString()
  );
}

function normalizeTeamKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');
}

function getFavoriteKey(value) {
  const key = normalizeTeamKey(value);
  return key ? `name:${key}` : '';
}

function getTeamFavoriteKeys(team) {
  return Array.from(
    new Set(
      [team?.name, team?.longName, ...(team?.aliases || [])]
        .map(getFavoriteKey)
        .filter(Boolean)
    )
  );
}

function readFavorites() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const stored = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];

    return Array.isArray(parsed)
      ? Array.from(
          new Set(
            parsed
              .filter((value) => typeof value === 'string')
              .map((value) => value.trim())
              .filter(Boolean)
          )
        )
      : [];
  } catch {
    return [];
  }
}

function saveFavorites(favorites) {
  try {
    window.localStorage.setItem(
      FAVORITES_STORAGE_KEY,
      JSON.stringify(favorites)
    );
    window.dispatchEvent(
      new CustomEvent(FAVORITES_EVENT, { detail: { favorites } })
    );
  } catch {
    // Favorites remain usable during the current session.
  }
}

function isFavorite(team, favoriteSet) {
  return getTeamFavoriteKeys(team).some((key) => favoriteSet.has(key));
}

function formatDate(value) {
  const text = String(value || '').trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${text}T12:00:00Z`));
}

function leaderMatchesSearch(leader, query) {
  if (!query) {
    return true;
  }

  return [
    leader?.name,
    leader?.team?.name,
    leader?.team?.longName,
    leader?.classYear,
    leader?.height,
    leader?.position,
    leader?.rankLabel,
    leader?.valueLabel,
    ...(leader?.team?.aliases || []),
  ].some((value) =>
    String(value || '').toLowerCase().includes(query)
  );
}

function FavoriteButton({ leader, active, onToggle, featured = false }) {
  return (
    <button
      type="button"
      className={`leaders-favorite${active ? ' active' : ''}${
        featured ? ' featured' : ''
      }`}
      aria-pressed={active}
      aria-label={
        active
          ? `Remove ${leader.team.name} from My Teams`
          : `Add ${leader.team.name} to My Teams`
      }
      onClick={(event) => {
        event.stopPropagation();
        onToggle(leader);
      }}
    >
      <span aria-hidden="true">★</span>
    </button>
  );
}

function LeaderIdentity({ leader }) {
  if (leader.type === 'team') {
    return (
      <>
        <strong>{leader.team.name}</strong>
        <small>
          {leader.sets !== null ? `${leader.sets} sets` : 'Division I team'}
        </small>
      </>
    );
  }

  const playerDetails = [
    leader.jersey ? `#${leader.jersey}` : '',
    leader.classYear,
    leader.height,
    leader.position,
    leader.sets !== null ? `${leader.sets} sets` : '',
  ].filter(Boolean);

  return (
    <>
      <strong>{leader.name}</strong>
      <small>{leader.team.name}</small>
      {playerDetails.length ? <em>{playerDetails.join(' · ')}</em> : null}
    </>
  );
}

function FeaturedLeader({ leader, category, favorite, onOpenTeam, onToggle }) {
  if (!leader) {
    return null;
  }

  return (
    <article className="leaders-featured">
      <button
        type="button"
        className="leaders-featured-open"
        onClick={() => onOpenTeam(leader.team)}
        aria-label={`Open ${leader.team.name} team profile`}
      >
        <span className="leaders-featured-rank">
          <small>{category?.mode === 'totals' ? 'Totals' : 'National'}</small>
          <strong>{leader.rankLabel}</strong>
        </span>

        <span className="leaders-featured-logo">
          <TeamLogo team={leader.team} variant="match-center" />
        </span>

        <span className="leaders-featured-copy">
          <p className="eyebrow coral">
            {leader.type === 'team' ? 'Team leader' : 'Individual leader'}
          </p>
          <h2>{leader.type === 'team' ? leader.team.name : leader.name}</h2>
          <span>
            {leader.type === 'team' ? category.label : leader.team.name}
          </span>
          {leader.type === 'individual' ? (
            <small>
              {[leader.jersey ? `#${leader.jersey}` : '', leader.classYear, leader.height, leader.position, leader.sets !== null ? `${leader.sets} sets` : '']
                .filter(Boolean)
                .join(' · ')}
            </small>
          ) : null}
        </span>

        <span className="leaders-featured-value">
          <small>{category.valueLabel || 'Value'}</small>
          <strong>{category.mode === 'totals' && Number.isFinite(leader.value) ? new Intl.NumberFormat().format(leader.value) : leader.valueLabel}</strong>
          {leader.totalLabel ? (
            <span>
              {leader.totalLabel} {category.totalLabel || ''}
            </span>
          ) : null}
        </span>
      </button>

      <FavoriteButton
        leader={leader}
        active={favorite}
        onToggle={onToggle}
        featured
      />
    </article>
  );
}

function LeaderRow({ leader, category, favorite, onOpenTeam, onToggle }) {
  return (
    <div className="leaders-row">
      <button
        type="button"
        className="leaders-row-open"
        onClick={() => onOpenTeam(leader.team)}
        aria-label={`Open ${leader.team.name} team profile`}
      >
        <span className="leaders-rank">
          <strong>{leader.rank}</strong>
          {leader.tied ? <small>T</small> : null}
        </span>

        <span className="leaders-logo">
          <TeamLogo team={leader.team} variant="scoreboard" />
        </span>

        <span className="leaders-identity">
          <LeaderIdentity leader={leader} />
        </span>

        <span className="leaders-total">
          <strong>{leader.totalLabel || '—'}</strong>
          <small>{category.totalLabel || 'Total'}</small>
        </span>

        <span className="leaders-value">
          <strong>{category.mode === 'totals' && Number.isFinite(leader.value) ? new Intl.NumberFormat().format(leader.value) : leader.valueLabel}</strong>
          <small>{category.valueLabel || 'Value'}</small>
        </span>

        <span className="leaders-arrow" aria-hidden="true">
          ›
        </span>
      </button>

      <FavoriteButton
        leader={leader}
        active={favorite}
        onToggle={onToggle}
      />
    </div>
  );
}

function LoadingState() {
  return <LoadingRows label="Loading statistical leaders…" />;
}

function ErrorState({ error, onRetry }) {
  return (
    <div className="leaders-state error" role="alert">
      <span className="leaders-state-icon" aria-hidden="true">
        !
      </span>
      <h2>We couldn’t load the leaders.</h2>
      <p>{error || 'Statistical leaders are temporarily unavailable.'}</p>
      <button type="button" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

export default function LeadersPage({ onOpenTeam }) {
  const initial = useMemo(readUrlState, []);
  const [type, setType] = useState(initial.type);
  const [categoryKey, setCategoryKey] = useState(initial.category);
  const [favorites, setFavorites] = useState(readFavorites);
  const [query, setQuery] = useState('');
  const [myTeams, setMyTeams] = useState(false);
  const [state, setState] = useState({
    loading: true,
    error: '',
    data: null,
  });
  const requestRef = useRef(0);

  const loadLeaders = useCallback(({ silent = false, force = false } = {}) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const controller = new AbortController();

    if (!silent) {
      setState((current) => ({
        ...current,
        loading: true,
        error: '',
      }));
    }

    getLeaders({
      type,
      category: categoryKey,
      signal: controller.signal,
      force: silent || force,
    })
      .then((data) => {
        if (requestRef.current !== requestId) {
          return;
        }

        setState({ loading: false, error: '', data });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || requestRef.current !== requestId) {
          return;
        }

        if (silent) return;

        setState({
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Statistical leaders are temporarily unavailable.',
          data: null,
        });
      });

    return () => controller.abort();
  }, [categoryKey, type]);

  useEffect(() => loadLeaders(), [loadLeaders]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') loadLeaders({ silent: true });
    };
    const interval = window.setInterval(refresh, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadLeaders]);

  useEffect(() => {
    writeUrlState({ type, category: categoryKey, mode: 'replace' });
  }, []);

  useEffect(() => {
    function handlePopState() {
      const next = readUrlState();
      setType(next.type);
      setCategoryKey(next.category);
    }

    function handleFavorites(event) {
      const next = Array.isArray(event?.detail?.favorites)
        ? event.detail.favorites
        : readFavorites();
      setFavorites(next);
    }

    window.addEventListener('popstate', handlePopState);
    window.addEventListener(FAVORITES_EVENT, handleFavorites);
    window.addEventListener('storage', handleFavorites);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener(FAVORITES_EVENT, handleFavorites);
      window.removeEventListener('storage', handleFavorites);
    };
  }, []);

  const categories = state.data?.categories?.length
    ? state.data.categories
    : FALLBACK_CATEGORIES[type];
  const selectedCategory =
    state.data?.category?.key === categoryKey
      ? state.data.category
      : categories.find((item) => item.key === categoryKey) || categories[0];
  const leaderMode = selectedCategory?.mode === 'totals' || categoryKey.startsWith('total-')
    ? 'totals'
    : 'per-set';
  const visibleCategories = categories.filter((category) =>
    leaderMode === 'totals'
      ? category.mode === 'totals' || category.key.startsWith('total-')
      : category.mode !== 'totals' && !category.key.startsWith('total-')
  );
  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);
  const leaders = state.data?.leaders || [];
  const leadersAvailable =
    state.data?.available === true &&
    Number(state.data?.season) === ACTIVE_WOMENS_SEASON &&
    leaders.length > 0;
  const visibleLeaders = leaders.filter((leader) => (!myTeams || isFavorite(leader.team, favoriteSet)) && `${leader.name} ${leader.team.name}`.toLowerCase().includes(query.trim().toLowerCase()));
  const featuredLeader = visibleLeaders[0] || null;
  const listLeaders = featuredLeader
    ? visibleLeaders.filter((leader) => leader.id !== featuredLeader.id)
    : [];

  function changeType(nextType) {
    const normalized = nextType === 'team' ? 'team' : 'individual';

    if (normalized === type) {
      return;
    }

    const nextCategory = leaderCategoryForType(categoryKey, normalized);
    setType(normalized);
    setCategoryKey(nextCategory);
    writeUrlState({
      type: normalized,
      category: nextCategory,
      mode: 'push',
    });
  }

  function changeCategory(nextCategory) {
    if (!nextCategory || nextCategory === categoryKey) {
      return;
    }

    setCategoryKey(nextCategory);
    writeUrlState({ type, category: nextCategory, mode: 'push' });
  }

  function changeMode(nextMode) {
    if (nextMode === leaderMode) return;
    const next = categories.find((category) =>
      nextMode === 'totals'
        ? category.mode === 'totals' || category.key.startsWith('total-')
        : category.mode !== 'totals' && !category.key.startsWith('total-')
    );
    if (next) changeCategory(next.key);
  }

  function toggleFavorite(leader) {
    const keys = getTeamFavoriteKeys(leader.team);
    const next = new Set(favorites);
    const active = keys.some((key) => next.has(key));

    if (active) {
      keys.forEach((key) => next.delete(key));
    } else {
      keys.forEach((key) => next.add(key));
    }

    const values = Array.from(next);
    setFavorites(values);
    saveFavorites(values);
  }

  return (
    <main className="leaders-page">
      <section className="leaders-section" id="leaders">
        <div className="shell">
          <div className="leaders-heading">
            <div>
              <p className="eyebrow coral">Women’s Division I</p>
              <h1>{ACTIVE_WOMENS_SEASON} Stat Leaders</h1>
            </div>

            {(
            <label className="leaders-category-picker">
              <span>Statistic</span>
              <select
                value={categoryKey}
                disabled={state.loading}
                onChange={(event) => changeCategory(event.target.value)}
              >
                {visibleCategories.map((category) => (
                  <option key={category.key} value={category.key}>
                    {category.label}
                  </option>
                ))}
              </select>
            </label>
            )}
          </div>

          {!state.loading && formatStatsThroughDate(state.data?.throughDate) ? <p className="leaders-data-through" role="status">Official NCAA statistics through {formatStatsThroughDate(state.data.throughDate)}. Later matches are not included until NCAA publishes an update.</p> : null}

          {(
          <>
          <div className="leaders-quick-controls">
            <input aria-label="Search leaders" type="search" value={query} placeholder="Search player or team" onChange={(event) => setQuery(event.target.value)} />
            <button type="button" aria-pressed={myTeams} onClick={() => setMyTeams((value) => !value)}>★ My Teams</button>
          </div>
          <div className="leaders-choice-row"><div className="leaders-type-tabs" role="tablist" aria-label="Leader type">
            <button
              type="button"
              role="tab"
              className={type === 'individual' ? 'active' : ''}
              aria-selected={type === 'individual'}
              onClick={() => changeType('individual')}
            >
              Individual
            </button>
            <button
              type="button"
              role="tab"
              className={type === 'team' ? 'active' : ''}
              aria-selected={type === 'team'}
              onClick={() => changeType('team')}
            >
              Team
            </button>
          </div>

          <div className="leaders-mode-toggle" role="group" aria-label="Leader calculation">
            <button
              type="button"
              className={leaderMode === 'per-set' ? 'active' : ''}
              aria-pressed={leaderMode === 'per-set'}
              onClick={() => changeMode('per-set')}
            >
              Rates / Pct.
            </button>
            <button
              type="button"
              className={leaderMode === 'totals' ? 'active' : ''}
              aria-pressed={leaderMode === 'totals'}
              onClick={() => changeMode('totals')}
            >
              Totals
            </button>
          </div>
          </div>
          {leaderMode === 'totals' ? <details className="leaders-methodology"><summary>About season totals</summary><p>Sorted by season totals across all pages of NCAA’s published tables, not just the first 50 rate leaders. {type === 'individual' ? 'Includes players who qualify for those tables, not every rostered player.' : 'Includes teams in the published tables.'} {selectedCategory?.key === 'total-points' && type === 'team' ? 'Points = kills + service aces + team blocks; this is not the sum of rally scores.' : ''}</p></details> : null}

          </>
          )}

          {state.loading ? (
            <LoadingState />
          ) : state.error ? (
            <ErrorState error={state.error} onRetry={() => loadLeaders({ force: true })} />
          ) : !leadersAvailable ? (
            <div className="leaders-state empty leaders-2026-empty" role="status">
              <span className="leaders-state-icon" aria-hidden="true">
                {ACTIVE_WOMENS_SEASON}
              </span>
              <h2>{state.data?.status === 'incomplete-published-tables' ? 'This totals table is temporarily incomplete.' : `Official ${ACTIVE_WOMENS_SEASON} leaders are not available for this category.`}</h2>
              <p>
                {state.data?.availabilityReason || 'Only official NCAA national tables are shown. Partial match samples are not ranked.'}
              </p>
              <button type="button" onClick={() => loadLeaders({ force: true })}>Retry official statistics</button>
            </div>
          ) : visibleLeaders.length ? (
            <>
              <FeaturedLeader
                leader={featuredLeader}
                category={selectedCategory}
                favorite={isFavorite(featuredLeader.team, favoriteSet)}
                onOpenTeam={onOpenTeam}
                onToggle={toggleFavorite}
              />

              <div className="leaders-list-heading">
                <strong>
                  {visibleLeaders.length} {type === 'team' ? 'teams' : 'players'} shown
                </strong>
                <span>{selectedCategory?.label}</span>
              </div>

              <section className="leaders-list" aria-label={leaderMode === 'totals' ? 'Published totals leaders' : 'National leaders'}>
                <div className="leaders-table-head" aria-hidden="true">
                  <span>Rank</span>
                  <span>{type === 'team' ? 'Team' : 'Player'}</span>
                  <span>{selectedCategory?.totalLabel || 'Total'}</span>
                  <span>{selectedCategory?.valueLabel || 'Value'}</span>
                </div>

                {listLeaders.map((leader) => (
                  <LeaderRow
                    key={leader.id}
                    leader={leader}
                    category={selectedCategory}
                    favorite={isFavorite(leader.team, favoriteSet)}
                    onOpenTeam={onOpenTeam}
                    onToggle={toggleFavorite}
                  />
                ))}
              </section>
            </>
          ) : (
            <div className="leaders-state" role="status"><p>No leaders match these filters.</p></div>
          )}
        </div>
      </section>
    </main>
  );
}
